import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Container, Card, Alert, Spinner, Row, Col, Tabs, Tab, Badge, ListGroup, Modal, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { getCatalogo, createCatalogo, updateCatalogo, getFilm, addFilmsToCatalogo, removeFilmFromCatalogo, reorderCatalogoFilms, getCatalogoFilms, getSuggerimentiGeneri, getSuggerimentiSupporti, getSuggerimentiCondizioni, getCatalogoOpzioni, anteprimaCatalogo, anteprimaPdfCatalogo, cercaPersoneCatalogo } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faArrowUp, faArrowDown, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Enum — valori lowercase, allineati al DB enum
const STATO_CATALOGO = {
    BOZZA: 'bozza',
    PUBBLICATO: 'pubblicato',
    ARCHIVIATO: 'archiviato'
};

const TEMPLATE_CATALOGO = {
    MODERNO: 'moderno',
    VINTAGE: 'vintage',
    MINIMAL: 'minimal',
    COLORFUL: 'colorful'
};

// Criteri di un catalogo dinamico: i film si ricalcolano a ogni anteprima/generazione
const CRITERI_DEFAULT = {
  generi: [],
  supporti: [],
  condizioni: [],
  registi: [],
  attori: [],
  attori_solo_protagonisti: true,
  attori_tutti: false,
  prezzo_min: null,
  prezzo_max: null,
  solo_disponibili: true,
  anno_min: null,
  anno_max: null,
  ordinamento: 'titolo',
  raggruppa_per_genere: true,
  unisci_doppioni: true,
  limite: null,
  schede_per_pagina: 6,
  includi_ids: [],
  escludi_ids: [],
};

const ORDINAMENTI = [
  { valore: 'titolo', etichetta: 'Titolo (A-Z)' },
  { valore: 'prezzo_asc', etichetta: 'Prezzo crescente' },
  { valore: 'prezzo_desc', etichetta: 'Prezzo decrescente' },
  { valore: 'rating', etichetta: 'Più votati' },
  { valore: 'anno_desc', etichetta: 'Più recenti' },
];

// Peso medio del PDF per film (MB) nelle tre qualità, misurato su cataloghi reali
const MB_PER_FILM = { low: 0.009, medium: 0.025, high: 0.07 };

const urlImmagine = (u) => (u && u.startsWith('http') ? u : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${u}`);
const euro = (v) => (v == null ? '-' : `€ ${Number(v).toFixed(2).replace('.', ',')}`);

// Scelta multipla di registi o attori con ricerca mentre si scrive (suggerimenti con il numero di titoli)
const SelettorePersone = ({ ruolo, valori, onChange, soloProtagonisti = true, placeholder }) => {
  const [testo, setTesto] = useState('');
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [aperto, setAperto] = useState(false);

  useEffect(() => {
    if (testo.trim().length < 2) {
      setSuggerimenti([]);
      return undefined;
    }
    let annullato = false;
    const timer = setTimeout(async () => {
      try {
        const res = await cercaPersoneCatalogo(ruolo, testo.trim(), soloProtagonisti);
        if (!annullato) setSuggerimenti(res.data.filter(p => !valori.includes(p.nome)));
      } catch (err) {
        console.error('Errore ricerca persone:', err);
      }
    }, 300);
    return () => {
      annullato = true;
      clearTimeout(timer);
    };
  }, [testo, ruolo, soloProtagonisti, valori]);

  const aggiungi = (nome) => {
    onChange([...valori, nome]);
    setTesto('');
    setSuggerimenti([]);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className="mb-1">
        {valori.map(nome => (
          <Badge key={nome} bg="primary" className="me-1 mb-1 fw-normal" style={{ cursor: 'pointer' }} title="Clicca per togliere" onClick={() => onChange(valori.filter(v => v !== nome))}>
            {nome} <FontAwesomeIcon icon={faTimes} />
          </Badge>
        ))}
      </div>
      <Form.Control
        size="sm" value={testo} placeholder={placeholder}
        onChange={(e) => { setTesto(e.target.value); setAperto(true); }}
        onFocus={() => setAperto(true)}
        onBlur={() => setTimeout(() => setAperto(false), 150)}
      />
      {aperto && suggerimenti.length > 0 && (
        <ListGroup style={{ position: 'absolute', zIndex: 20, width: '100%', maxHeight: '240px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
          {suggerimenti.map(p => (
            <ListGroup.Item action key={p.nome} onMouseDown={(e) => { e.preventDefault(); aggiungi(p.nome); }} className="py-1 d-flex justify-content-between">
              <span>{p.nome}</span><span className="text-muted small">{p.n} titoli</span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

// Miniature dei template: riproducono colori, bordi e intestazione del PDF
const TEMPLATE_ANTEPRIMA = {
  moderno: { bg: '#F1F5F9', card: '#FFFFFF', border: '#E2E8F0', primary: '#1D4ED8', chip: '#DBEAFE', radius: 6, doppia: false, centro: false },
  vintage: { bg: '#F4E8CF', card: '#FFFAEC', border: '#D6BC8A', primary: '#8B1E1E', chip: '#F0DCB0', radius: 2, doppia: true, centro: true, accent: '#C8963E' },
  minimal: { bg: '#FFFFFF', card: '#FFFFFF', border: '#CBD5E1', primary: '#111827', chip: '#F3F4F6', radius: 2, doppia: false, centro: false },
  colorful: { bg: '#FFF7ED', card: '#FFFFFF', border: '#FED7AA', primary: '#EA580C', chip: '#CFFAFE', radius: 9, doppia: false, centro: false },
};

const TemplateAnteprima = ({ nome }) => {
  const t = TEMPLATE_ANTEPRIMA[nome] || TEMPLATE_ANTEPRIMA.moderno;
  return (
    <div style={{
      width: 150, height: 190, background: t.bg, padding: 8, position: 'relative', overflow: 'hidden', borderRadius: 4,
      border: t.doppia ? `3px double ${t.accent}` : '1px solid #d0d5dd',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, background: t.primary }} />
      <div style={{ marginTop: 8, textAlign: t.centro ? 'center' : 'left', fontWeight: 700, fontSize: 11, color: '#222' }}>Azione</div>
      <div style={{ height: 1, background: t.border, margin: '3px 0 6px' }} />
      {[0, 1].map(i => (
        <div key={i} style={{ display: 'flex', gap: 4, background: t.card, border: `1px solid ${t.border}`, borderRadius: t.radius, padding: 4, marginBottom: 5 }}>
          <div style={{ width: 26, height: 38, background: '#cbd5e1', borderRadius: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 5, width: '60%', background: t.chip, borderRadius: 3, marginBottom: 3 }} />
            <div style={{ height: 5, width: '85%', background: '#334155', opacity: 0.8, borderRadius: 2, marginBottom: 3 }} />
            <div style={{ height: 3, width: '95%', background: '#cbd5e1', marginBottom: 2 }} />
            <div style={{ height: 3, width: '70%', background: '#cbd5e1' }} />
            <div style={{ height: 8, width: 28, background: t.primary, borderRadius: 5, marginTop: 3, marginLeft: 'auto' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente per item film draggable
const DraggableFilmItem = ({ film, index, moveFilm, onRemove }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'film',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'film',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveFilm(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <ListGroup.Item
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        padding: '10px',
        marginBottom: '5px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <strong>{film.titolo}</strong>
          <br />
          <small className="text-muted">
            {film.anno && `${film.anno} • `}
            {film.genere && `${film.genere} • `}
            {film.supporto && film.supporto}
          </small>
        </div>
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => onRemove(film.id)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </div>
    </ListGroup.Item>
  );
};

function CatalogoEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = id !== undefined;
  const [activeTab, setActiveTab] = useState('info');

  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    data_validita_inizio: null,
    data_validita_fine: null,
    template: TEMPLATE_CATALOGO.MODERNO,
    stato: STATO_CATALOGO.BOZZA,
    mostra_logo: true,
    mostra_qr_code: true,
    testo_header: '',
    testo_footer: '',
    mostra_produttore: false,
    mostra_ean: false,
    mostra_note: false,
  });

  const [selectedFilms, setSelectedFilms] = useState([]);

  // Catalogo dinamico (i nuovi cataloghi lo sono sempre; i vecchi a lista manuale restano com'erano)
  const [dinamico, setDinamico] = useState(!isEditMode);
  const [criteri, setCriteri] = useState(CRITERI_DEFAULT);
  const [opzioni, setOpzioni] = useState({ generi: [], supporti: [], condizioni: [], prezzo_min: null, prezzo_max: null });
  const [anteprima, setAnteprima] = useState(null);
  const [loadingAnteprima, setLoadingAnteprima] = useState(false);
  const [filmSearchQuery, setFilmSearchQuery] = useState('');
  const [filmSearchResults, setFilmSearchResults] = useState([]);
  const [searchingFilms, setSearchingFilms] = useState(false);
  const [showAddFilmsModal, setShowAddFilmsModal] = useState(false);
  
  // Stati per ricerca avanzata
  const [searchMode, setSearchMode] = useState('quick'); // 'quick' o 'advanced'
  const [advancedFilters, setAdvancedFilters] = useState({
    titolo: '',
    attori: '',
    regista: '',
    genere: '',
    supporto: '',
    condizione: '',
    anno_min: '',
    anno_max: '',
    fornitore: '',
    fuori_catalogo: '',
  });
  const [advancedSearchResults, setAdvancedSearchResults] = useState([]);
  const [selectedFilmIdsForBulkAdd, setSelectedFilmIdsForBulkAdd] = useState([]);
  const [searchingAdvanced, setSearchingAdvanced] = useState(false);
  
  // Stati per dropdown filtri
  const [generiOptions, setGeneriOptions] = useState([]);
  const [supportiOptions, setSupportiOptions] = useState([]);
  const [condizioniOptions, setCondizioniOptions] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  
  // Stati per sort tabella risultati
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      const fetchCatalogo = async () => {
        try {
          const response = await getCatalogo(id);
          const catalogoData = response.data;

          setFormData({
            nome: catalogoData.nome || '',
            descrizione: catalogoData.descrizione || '',
            data_validita_inizio: catalogoData.data_validita_inizio ? new Date(catalogoData.data_validita_inizio) : null,
            data_validita_fine: catalogoData.data_validita_fine ? new Date(catalogoData.data_validita_fine) : null,
            template: catalogoData.template || TEMPLATE_CATALOGO.MODERNO,
            stato: catalogoData.stato || STATO_CATALOGO.BOZZA,
            mostra_logo: catalogoData.mostra_logo !== undefined ? catalogoData.mostra_logo : true,
            mostra_qr_code: catalogoData.mostra_qr_code !== undefined ? catalogoData.mostra_qr_code : true,
            testo_header: catalogoData.testo_header || '',
            testo_footer: catalogoData.testo_footer || '',
            mostra_produttore: catalogoData.mostra_produttore || false,
            mostra_ean: catalogoData.mostra_ean || false,
            mostra_note: catalogoData.mostra_note || false,
          });

          if (catalogoData.criteri) {
            setDinamico(true);
            setCriteri({ ...CRITERI_DEFAULT, ...catalogoData.criteri });
            setLoading(false);
            return;
          }
          setDinamico(false);

          // Carica film ordinati
          const filmsResponse = await getCatalogoFilms(id, true);
          setSelectedFilms(filmsResponse.data || []);
          
          setLoading(false);
        } catch (err) {
          setError('Errore durante il caricamento del catalogo.');
          setLoading(false);
          console.error(err);
        }
      };
      fetchCatalogo();
    } else {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    if (!dinamico) return;
    getCatalogoOpzioni()
      .then((res) => setOpzioni(res.data))
      .catch((err) => console.error('Errore caricamento opzioni criteri:', err));
  }, [dinamico]);

  // Anteprima in tempo reale (con attesa di mezzo secondo dopo l'ultima modifica)
  useEffect(() => {
    if (!dinamico || loading) return undefined;
    let annullato = false;
    const timer = setTimeout(async () => {
      setLoadingAnteprima(true);
      try {
        const res = await anteprimaCatalogo(criteri);
        if (!annullato) setAnteprima(res.data);
      } catch (err) {
        if (!annullato) setError('Errore durante il calcolo dell\'anteprima.');
        console.error(err);
      } finally {
        if (!annullato) setLoadingAnteprima(false);
      }
    }, 500);
    return () => {
      annullato = true;
      clearTimeout(timer);
    };
  }, [criteri, dinamico, loading]);

  // PDF di prova dai criteri e dallo stile correnti, senza salvare il catalogo
  const [generandoAnteprimaPdf, setGenerandoAnteprimaPdf] = useState(false);
  const apriAnteprimaPdf = async () => {
    const finestra = window.open('', '_blank'); // aperta subito per non farla bloccare dal browser
    try {
      setGenerandoAnteprimaPdf(true);
      setError(null);
      const res = await anteprimaPdfCatalogo({
        ...formData,
        nome: formData.nome.trim() || 'Anteprima catalogo',
        data_validita_inizio: formData.data_validita_inizio ? formData.data_validita_inizio.toISOString().split('T')[0] : null,
        data_validita_fine: formData.data_validita_fine ? formData.data_validita_fine.toISOString().split('T')[0] : null,
        criteri,
      });
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${res.data.pdf_path}`;
      if (finestra) finestra.location = url; else window.open(url, '_blank');
    } catch (err) {
      if (finestra) finestra.close();
      setError('Anteprima PDF non riuscita: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerandoAnteprimaPdf(false);
    }
  };

  const aggiornaCriteri = (campo, valore) => setCriteri(prev => ({ ...prev, [campo]: valore }));
  const numeroOpzionale = (v) => (v === '' ? null : Number(v));
  const toggleInLista = (campo, valore) => setCriteri(prev => ({
    ...prev,
    [campo]: prev[campo].includes(valore) ? prev[campo].filter(v => v !== valore) : [...prev[campo], valore],
  }));
  const escludiFilm = (filmId) => setCriteri(prev => ({
    ...prev,
    escludi_ids: [...prev.escludi_ids, filmId],
    includi_ids: prev.includi_ids.filter(x => x !== filmId),
  }));
  const ripristinaFilm = (filmId) => setCriteri(prev => ({ ...prev, escludi_ids: prev.escludi_ids.filter(x => x !== filmId) }));
  const rimuoviInclusione = (filmId) => setCriteri(prev => ({ ...prev, includi_ids: prev.includi_ids.filter(x => x !== filmId) }));
  const aggiungiExtra = (ids) => setCriteri(prev => ({
    ...prev,
    includi_ids: [...new Set([...prev.includi_ids, ...ids])],
    escludi_ids: prev.escludi_ids.filter(x => !ids.includes(x)),
  }));
  const giaNelCatalogo = (film) => (dinamico
    ? criteri.includi_ids.includes(film.id)
    : selectedFilms.some(f => f.id === film.id));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : 
                      (type === 'number' ? (value === '' ? null : parseInt(value, 10)) : value);
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleDateChange = (date, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  // Carica dropdown per filtri avanzati
  const loadDropdownOptions = async () => {
    if (loadingDropdowns) return;
    
    try {
      setLoadingDropdowns(true);
      const [generiRes, supportiRes, condizioniRes] = await Promise.all([
        getSuggerimentiGeneri(),
        getSuggerimentiSupporti(), 
        getSuggerimentiCondizioni()
      ]);
      
      setGeneriOptions(generiRes.data || []);
      setSupportiOptions(supportiRes.data || []);
      setCondizioniOptions(condizioniRes.data || []);
    } catch (err) {
      console.error('Errore caricamento dropdown:', err);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Gestione sort della tabella risultati
  const handleSort = (column) => {
    if (sortBy === column) {
      // Se stessa colonna, cambia direzione
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nuova colonna, imposta ascendente
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Applica sort sui risultati ogni volta che cambiano sortBy o sortOrder
  useEffect(() => {
    if (sortBy && advancedSearchResults.length > 0) {
      const sortedResults = [...advancedSearchResults].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        
        // Gestione valori null/undefined
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        // Sort numerico per anno
        if (sortBy === 'anno') {
          const aNum = parseInt(aVal) || 0;
          const bNum = parseInt(bVal) || 0;
          return sortOrder === 'desc' ? bNum - aNum : aNum - bNum;
        }
        
        // Sort alfabetico per altri campi
        const comparison = aVal.toString().localeCompare(bVal.toString(), 'it', { 
          numeric: true,
          sensitivity: 'base'
        });
        return sortOrder === 'desc' ? -comparison : comparison;
      });
      setAdvancedSearchResults(sortedResults);
    }
  }, [sortBy, sortOrder]); // Solo questi deps per evitare loop infinito

  // Ricerca avanzata con filtri multipli
  const handleAdvancedSearch = async () => {
    try {
      setSearchingAdvanced(true);
      const filters = {};
      
      if (advancedFilters.titolo) {
        filters.titolo__ilike = advancedFilters.titolo;
      }
      if (advancedFilters.attori) {
        filters.attori__ilike = advancedFilters.attori;
      }
      if (advancedFilters.regista) {
        filters.regista__ilike = advancedFilters.regista;
      }
      if (advancedFilters.genere) {
        filters.genere__ilike = advancedFilters.genere;
      }
      if (advancedFilters.supporto) {
        filters.supporto__eq = advancedFilters.supporto;
      }
      if (advancedFilters.condizione) {
        filters.condizione__eq = advancedFilters.condizione;
      }
      if (advancedFilters.anno_min) {
        filters.anno__gte = parseInt(advancedFilters.anno_min, 10);
      }
      if (advancedFilters.anno_max) {
        filters.anno__lte = parseInt(advancedFilters.anno_max, 10);
      }
      if (advancedFilters.fornitore) {
        filters.fornitore__ilike = advancedFilters.fornitore;
      }
      if (advancedFilters.fuori_catalogo !== '') {
        filters.fuori_catalogo__eq = advancedFilters.fuori_catalogo === 'true';
      }

      // Aumentato limite da 100 a 1000 per mostrare tutti i risultati
      const response = await getFilm(0, 1000, [], JSON.stringify(filters));
      const results = response.data.film || [];
      
      setAdvancedSearchResults(results);
      setSelectedFilmIdsForBulkAdd([]);
      // Il sort sarà applicato dall'useEffect dedicato se sortBy è impostato
    } catch (err) {
      setError('Errore durante la ricerca avanzata.');
      console.error(err);
    } finally {
      setSearchingAdvanced(false);
    }
  };

  const handleToggleFilmSelection = (filmId) => {
    setSelectedFilmIdsForBulkAdd(prev => {
      if (prev.includes(filmId)) {
        return prev.filter(id => id !== filmId);
      } else {
        return [...prev, filmId];
      }
    });
  };

  const handleToggleAllFilms = () => {
    if (selectedFilmIdsForBulkAdd.length === advancedSearchResults.length) {
      setSelectedFilmIdsForBulkAdd([]);
    } else {
      setSelectedFilmIdsForBulkAdd(advancedSearchResults.map(f => f.id));
    }
  };

  const handleAddSelectedFilms = () => {
    if (dinamico) {
      aggiungiExtra(selectedFilmIdsForBulkAdd);
      setSelectedFilmIdsForBulkAdd([]);
      setAdvancedSearchResults([]);
      setShowAddFilmsModal(false);
      return;
    }
    const filmsToAdd = advancedSearchResults.filter(f => selectedFilmIdsForBulkAdd.includes(f.id));
    filmsToAdd.forEach(film => {
      if (!selectedFilms.find(f => f.id === film.id)) {
        setSelectedFilms(prev => [...prev, film]);
      }
    });
    setSelectedFilmIdsForBulkAdd([]);
    setAdvancedSearchResults([]);
    setShowAddFilmsModal(false);
  };

  // Apre modal aggiunta film in modalità normale
  const openAddFilmsModal = () => {
    setShowAddFilmsModal(true);
    // Se non ho ancora caricato i dropdown, carica
    if (generiOptions.length === 0) {
      loadDropdownOptions();
    }
  };

  // Carica dropdown quando si cambia in modalità avanzata
  useEffect(() => {
    if (searchMode === 'advanced' && showAddFilmsModal && generiOptions.length === 0) {
      loadDropdownOptions();
    }
  }, [searchMode, showAddFilmsModal, generiOptions.length]);

  const handleCloseAddFilmsModal = () => {
    setShowAddFilmsModal(false);
    setFilmSearchQuery('');
    setFilmSearchResults([]);
    setAdvancedSearchResults([]);
    setSelectedFilmIdsForBulkAdd([]);
    setSearchMode('quick');
  };

  const handleSearchFilms = async () => {
    if (!filmSearchQuery.trim()) return;
    
    try {
      setSearchingFilms(true);
      const filters = { titolo__ilike: filmSearchQuery };
      const response = await getFilm(0, 50, [], JSON.stringify(filters));
      setFilmSearchResults(response.data.film || []);
    } catch (err) {
      setError('Errore durante la ricerca dei film.');
      console.error(err);
    } finally {
      setSearchingFilms(false);
    }
  };

  const handleAddFilm = (film) => {
    if (dinamico) {
      aggiungiExtra([film.id]);
      setShowAddFilmsModal(false);
      setFilmSearchQuery('');
      setFilmSearchResults([]);
      return;
    }
    if (!selectedFilms.find(f => f.id === film.id)) {
      setSelectedFilms(prev => [...prev, film]);
    }
    setShowAddFilmsModal(false);
    setFilmSearchQuery('');
    setFilmSearchResults([]);
  };

  const handleRemoveFilm = (filmId) => {
    setSelectedFilms(prev => prev.filter(f => f.id !== filmId));
  };

  const moveFilm = useCallback((fromIndex, toIndex) => {
    setSelectedFilms(prev => {
      const updated = [...prev];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      setError('Il nome del catalogo è obbligatorio.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const catalogoData = {
        ...formData,
        data_validita_inizio: formData.data_validita_inizio 
          ? formData.data_validita_inizio.toISOString().split('T')[0] 
          : null,
        data_validita_fine: formData.data_validita_fine 
          ? formData.data_validita_fine.toISOString().split('T')[0] 
          : null,
        ...(dinamico ? { criteri, film_ids: [] } : { film_ids: selectedFilms.map(f => f.id) }),
      };

      if (isEditMode) {
        await updateCatalogo(id, catalogoData);
        setSuccess('Catalogo aggiornato con successo!');
      } else {
        const response = await createCatalogo(catalogoData);
        setSuccess('Catalogo creato con successo!');
        navigate(`/cataloghi/edit/${response.data.id}`);
      }
    } catch (err) {
      setError('Errore durante il salvataggio del catalogo: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Caricamento catalogo...</p>
      </Container>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Container fluid className="p-4">
        <h2 className="mb-4">{isEditMode ? 'Modifica Catalogo' : 'Nuovo Catalogo'}</h2>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
            {/* TAB 1: Informazioni Base */}
            <Tab eventKey="info" title="📋 Informazioni">
              <Card>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Nome Catalogo *</Form.Label>
                        <Form.Control
                          type="text"
                          name="nome"
                          value={formData.nome}
                          onChange={handleChange}
                          required
                          placeholder="es. Catalogo Natale 2026"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stato</Form.Label>
                        <Form.Select name="stato" value={formData.stato} onChange={handleChange}>
                          <option value="bozza">Bozza</option>
                          <option value="pubblicato">Pubblicato</option>
                          <option value="archiviato">Archiviato</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Descrizione</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="descrizione"
                      value={formData.descrizione}
                      onChange={handleChange}
                      placeholder="Descrizione del catalogo (opzionale)"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Valido dal</Form.Label>
                        <DatePicker
                          selected={formData.data_validita_inizio}
                          onChange={(date) => handleDateChange(date, 'data_validita_inizio')}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Seleziona data inizio"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Valido fino al</Form.Label>
                        <DatePicker
                          selected={formData.data_validita_fine}
                          onChange={(date) => handleDateChange(date, 'data_validita_fine')}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Seleziona data fine"
                          isClearable
                          minDate={formData.data_validita_inizio}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* TAB: Criteri di selezione (catalogo dinamico) */}
            {dinamico && (
            <Tab eventKey="selezione" title={`🎯 Selezione${anteprima ? ` (${anteprima.totale})` : ''}`}>
              <Row>
                <Col lg={5}>
                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Generi</h6>
                        <div className="small">
                          <Button variant="link" size="sm" className="p-0 me-3" onClick={() => aggiornaCriteri('generi', opzioni.generi.map(g => g.valore))}>seleziona tutti</Button>
                          <Button variant="link" size="sm" className="p-0" onClick={() => aggiornaCriteri('generi', [])}>azzera</Button>
                        </div>
                      </div>
                      <div style={{ maxHeight: '230px', overflowY: 'auto' }}>
                        {opzioni.generi.map(g => (
                          <Form.Check
                            key={g.valore}
                            type="checkbox"
                            id={`genere-${g.valore}`}
                            checked={criteri.generi.includes(g.valore)}
                            onChange={() => toggleInLista('generi', g.valore)}
                            label={<>{g.valore} <span className="text-muted small">({g.n})</span></>}
                          />
                        ))}
                      </div>
                      <div className="form-text">Nessun genere spuntato = tutti i generi.</div>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Body>
                      <h6>Registi</h6>
                      <SelettorePersone
                        ruolo="registi" valori={criteri.registi} onChange={(v) => aggiornaCriteri('registi', v)}
                        placeholder="Scrivi un nome (es. Hitchcock) e scegli dall'elenco"
                      />
                      <div className="form-text mb-3">Film di almeno uno dei registi scelti.</div>

                      <h6>Attori</h6>
                      <SelettorePersone
                        ruolo="attori" valori={criteri.attori} onChange={(v) => aggiornaCriteri('attori', v)}
                        soloProtagonisti={criteri.attori_solo_protagonisti}
                        placeholder="Scrivi un nome (es. Eastwood) e scegli dall'elenco"
                      />
                      <Form.Check
                        className="mt-2" type="switch" id="attori-protagonisti"
                        checked={criteri.attori_solo_protagonisti}
                        onChange={(e) => aggiornaCriteri('attori_solo_protagonisti', e.target.checked)}
                        label="Solo i protagonisti (primi 3 del cast), senza i cameo"
                      />
                      <Form.Check
                        type="switch" id="attori-tutti"
                        checked={criteri.attori_tutti}
                        onChange={(e) => aggiornaCriteri('attori_tutti', e.target.checked)}
                        label="Solo film con tutti gli attori scelti insieme"
                      />
                      <div className="form-text">Se non spunti "tutti insieme", basta uno degli attori scelti.</div>
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Body>
                      <h6>Supporto</h6>
                      {opzioni.supporti.map(sp => (
                        <Form.Check
                          key={sp.valore}
                          inline
                          type="checkbox"
                          id={`supporto-${sp.valore}`}
                          checked={criteri.supporti.includes(sp.valore)}
                          onChange={() => toggleInLista('supporti', sp.valore)}
                          label={<>{sp.valore} <span className="text-muted small">({sp.n})</span></>}
                        />
                      ))}
                      <div className="form-text">Nessun supporto spuntato = tutti.</div>

                      <hr />
                      <h6>Condizione</h6>
                      {opzioni.condizioni.map(co => (
                        <Form.Check
                          key={co.valore}
                          inline
                          type="checkbox"
                          id={`condizione-${co.valore}`}
                          checked={criteri.condizioni.includes(co.valore)}
                          onChange={() => toggleInLista('condizioni', co.valore)}
                          label={<>{co.etichetta} <span className="text-muted small">({co.n})</span></>}
                        />
                      ))}
                      <div className="form-text">Nessuna condizione spuntata = tutte.</div>
                      <Form.Check
                        className="mt-2"
                        type="switch"
                        id="unisci-doppioni"
                        checked={criteri.unisci_doppioni}
                        onChange={(e) => aggiornaCriteri('unisci_doppioni', e.target.checked)}
                        label="Un solo esemplare per titolo e supporto (tiene la condizione migliore)"
                      />

                      <hr />
                      <h6>Prezzo</h6>
                      <Row>
                        <Col xs={6}>
                          <Form.Label className="small mb-1">Da (€)</Form.Label>
                          <Form.Control
                            type="number" min="0" step="0.5"
                            value={criteri.prezzo_min ?? ''}
                            placeholder="nessun minimo"
                            onChange={(e) => aggiornaCriteri('prezzo_min', numeroOpzionale(e.target.value))}
                          />
                        </Col>
                        <Col xs={6}>
                          <Form.Label className="small mb-1">A (€)</Form.Label>
                          <Form.Control
                            type="number" min="0" step="0.5"
                            value={criteri.prezzo_max ?? ''}
                            placeholder="nessun massimo"
                            onChange={(e) => aggiornaCriteri('prezzo_max', numeroOpzionale(e.target.value))}
                          />
                        </Col>
                      </Row>
                      {opzioni.prezzo_min != null && (
                        <div className="form-text">Prezzi in magazzino: da {euro(opzioni.prezzo_min)} a {euro(opzioni.prezzo_max)}. Lascia vuoto per non filtrare.</div>
                      )}

                      <hr />
                      <h6>Anno di uscita</h6>
                      <Row>
                        <Col xs={6}>
                          <Form.Control
                            type="number" placeholder="da" value={criteri.anno_min ?? ''}
                            onChange={(e) => aggiornaCriteri('anno_min', numeroOpzionale(e.target.value))}
                          />
                        </Col>
                        <Col xs={6}>
                          <Form.Control
                            type="number" placeholder="a" value={criteri.anno_max ?? ''}
                            onChange={(e) => aggiornaCriteri('anno_max', numeroOpzionale(e.target.value))}
                          />
                        </Col>
                      </Row>

                      <Form.Check
                        className="mt-3"
                        type="switch"
                        id="solo-disponibili"
                        checked={criteri.solo_disponibili}
                        onChange={(e) => aggiornaCriteri('solo_disponibili', e.target.checked)}
                        label="Solo titoli disponibili (in magazzino e non fuori catalogo)"
                      />
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Body>
                      <h6>Impaginazione</h6>
                      <Form.Label className="small mb-1">Schede per pagina</Form.Label>
                      <div className="mb-3">
                        {[4, 6, 8].map(n => (
                          <Button
                            key={n}
                            size="sm"
                            className="me-2"
                            variant={criteri.schede_per_pagina === n ? 'primary' : 'outline-primary'}
                            onClick={() => aggiornaCriteri('schede_per_pagina', n)}
                          >
                            {n}
                          </Button>
                        ))}
                        <span className="text-muted small">4 = più leggibile da telefono, 8 = più compatto</span>
                      </div>
                      <Form.Check
                        type="switch"
                        id="raggruppa-genere"
                        className="mb-3"
                        checked={criteri.raggruppa_per_genere}
                        onChange={(e) => aggiornaCriteri('raggruppa_per_genere', e.target.checked)}
                        label="Una sezione per genere, con indice cliccabile"
                      />
                      <Row>
                        <Col xs={7}>
                          <Form.Label className="small mb-1">Ordinamento</Form.Label>
                          <Form.Select value={criteri.ordinamento} onChange={(e) => aggiornaCriteri('ordinamento', e.target.value)}>
                            {ORDINAMENTI.map(o => <option key={o.valore} value={o.valore}>{o.etichetta}</option>)}
                          </Form.Select>
                        </Col>
                        <Col xs={5}>
                          <Form.Label className="small mb-1">Massimo film</Form.Label>
                          <Form.Control
                            type="number" min="1" placeholder="nessun limite" value={criteri.limite ?? ''}
                            onChange={(e) => aggiornaCriteri('limite', numeroOpzionale(e.target.value))}
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={7}>
                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                        <div>
                          <span className="fs-3 fw-bold">{anteprima ? anteprima.totale : '…'}</span> titoli
                          {anteprima && <span className="text-muted"> · circa {anteprima.pagine} pagine</span>}
                        </div>
                        {loadingAnteprima && <Spinner animation="border" size="sm" />}
                      </div>

                      {anteprima && anteprima.totale > 0 && (
                        <>
                          <div className="mb-2">
                            {anteprima.per_supporto.map(sp => (
                              <Badge key={sp.supporto} bg="secondary" className="me-1">{sp.supporto}: {sp.n}</Badge>
                            ))}
                          </div>
                          <div className="small text-muted mb-2">
                            Peso stimato del PDF: WhatsApp ≈ {Math.max(0.3, anteprima.totale * MB_PER_FILM.low).toFixed(1)} MB
                            {' · '}email ≈ {Math.max(0.3, anteprima.totale * MB_PER_FILM.medium).toFixed(1)} MB
                            {' · '}stampa ≈ {Math.max(0.3, anteprima.totale * MB_PER_FILM.high).toFixed(1)} MB
                          </div>
                          {anteprima.totale * MB_PER_FILM.medium > 22 && (
                            <Alert variant="warning" className="py-2 small">
                              Con tanti titoli il PDF per email supera i limiti degli allegati: restringi generi, prezzo o imposta un massimo di film.
                            </Alert>
                          )}
                          {(anteprima.senza_trama > 0 || anteprima.senza_immagine > 0) && (
                            <div className="small text-muted mb-2">
                              {anteprima.senza_trama > 0 && <>{anteprima.senza_trama} senza trama. </>}
                              {anteprima.senza_immagine > 0 && <>{anteprima.senza_immagine} senza locandina (verrà usato un segnaposto).</>}
                            </div>
                          )}
                        </>
                      )}

                      {anteprima && anteprima.totale === 0 && (
                        <Alert variant="info" className="mb-0">Nessun film corrisponde ai criteri.</Alert>
                      )}

                      {anteprima && anteprima.righe.length > 0 && (
                        <div style={{ maxHeight: '460px', overflowY: 'auto' }} className="border rounded">
                          <Table size="sm" hover className="mb-0 align-middle">
                            <tbody>
                              {anteprima.righe.map(f => (
                                <tr key={f.id}>
                                  <td style={{ width: '40px' }}>
                                    {f.immagine_url && (
                                      <img src={urlImmagine(f.immagine_url)} alt="" style={{ width: '28px', height: '42px', objectFit: 'cover', borderRadius: '3px' }} onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                    )}
                                  </td>
                                  <td>
                                    <strong>{f.titolo}</strong>
                                    <div className="small text-muted">{[f.anno, f.genere].filter(Boolean).join(' • ')}</div>
                                  </td>
                                  <td><Badge bg={f.supporto && f.supporto.includes('4K') ? 'warning' : f.supporto && f.supporto.toUpperCase().includes('BLU') ? 'info' : 'dark'}>{f.supporto || 'N/D'}</Badge></td>
                                  <td className="text-end">{euro(f.prezzo)}</td>
                                  <td className="text-end" style={{ width: '40px' }}>
                                    <Button size="sm" variant="outline-danger" title="Escludi dal catalogo" onClick={() => escludiFilm(f.id)}>
                                      <FontAwesomeIcon icon={faTimes} />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      {anteprima && anteprima.troncato && (
                        <div className="small text-muted mt-1">Mostrati i primi 300 titoli: il PDF li conterrà tutti.</div>
                      )}
                    </Card.Body>
                  </Card>

                  <Card className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Eccezioni manuali</h6>
                        <Button size="sm" variant="outline-primary" onClick={openAddFilmsModal}>
                          <FontAwesomeIcon icon={faPlus} /> Aggiungi film extra
                        </Button>
                      </div>
                      {(!anteprima || (anteprima.inclusi.length === 0 && anteprima.esclusi.length === 0)) && (
                        <div className="small text-muted">Nessuna. Usa la ✕ nell'elenco per togliere un titolo, o aggiungi film che i criteri non includono.</div>
                      )}
                      {anteprima && anteprima.inclusi.length > 0 && (
                        <div className="mb-2">
                          <div className="small text-muted mb-1">Aggiunti a mano ({anteprima.inclusi.length})</div>
                          {anteprima.inclusi.map(f => (
                            <Badge key={f.id} bg="success" className="me-1 mb-1 fw-normal" style={{ cursor: 'pointer' }} title="Clicca per rimuovere" onClick={() => rimuoviInclusione(f.id)}>
                              {f.titolo} ({f.supporto || 'N/D'}) <FontAwesomeIcon icon={faTimes} />
                            </Badge>
                          ))}
                        </div>
                      )}
                      {anteprima && anteprima.esclusi.length > 0 && (
                        <div>
                          <div className="small text-muted mb-1">Esclusi ({anteprima.esclusi.length}) — clicca per riammettere</div>
                          {anteprima.esclusi.map(f => (
                            <Badge key={f.id} bg="secondary" className="me-1 mb-1 fw-normal" style={{ cursor: 'pointer' }} title="Clicca per riammettere" onClick={() => ripristinaFilm(f.id)}>
                              {f.titolo} ({f.supporto || 'N/D'}) <FontAwesomeIcon icon={faTimes} />
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
            )}

            {/* TAB 2: Template e Layout */}
            <Tab eventKey="style" title="🎨 Stile">
              <Card>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Template Grafico</Form.Label>
                    <div className="d-flex gap-3 flex-wrap">
                      {Object.values(TEMPLATE_CATALOGO).map(template => (
                        <Form.Check
                          key={template}
                          type="radio"
                          name="template"
                          value={template}
                          checked={formData.template === template}
                          onChange={handleChange}
                          label={
                            <div style={{ textAlign: 'center' }}>
                              <TemplateAnteprima nome={template} />
                              <div className="fw-bold mt-1">{template.toUpperCase()}</div>
                              <div className="text-muted small mt-1">
                                {template === 'moderno' && 'Design pulito e vivace'}
                                {template === 'vintage' && 'Stile retrò classico'}
                                {template === 'minimal' && 'Essenziale e elegante'}
                                {template === 'colorful' && 'Colorato e dinamico'}
                              </div>
                            </div>
                          }
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Tab>

            {/* TAB 3: Opzioni Header/Footer */}
            <Tab eventKey="options" title="⚙️ Opzioni">
              <Card>
                <Card.Body>
                  <h5>Elementi Header/Footer</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        name="mostra_logo"
                        checked={formData.mostra_logo}
                        onChange={handleChange}
                        label="Mostra logo aziendale nell'header"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        name="mostra_qr_code"
                        checked={formData.mostra_qr_code}
                        onChange={handleChange}
                        label="Mostra QR code nel footer"
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Testo Header (opzionale)</Form.Label>
                    <Form.Control
                      type="text"
                      name="testo_header"
                      value={formData.testo_header}
                      onChange={handleChange}
                      placeholder="es. Catalogo Promozionale Natale 2026"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Testo Footer (opzionale)</Form.Label>
                    <Form.Control
                      type="text"
                      name="testo_footer"
                      value={formData.testo_footer}
                      onChange={handleChange}
                      placeholder="es. Prezzi validi fino ad esaurimento scorte"
                    />
                  </Form.Group>

                  <hr />

                  <h5>Campi Prodotto Opzionali</h5>
                  <Row>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        name="mostra_produttore"
                        checked={formData.mostra_produttore}
                        onChange={handleChange}
                        label="Mostra produttore"
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        name="mostra_ean"
                        checked={formData.mostra_ean}
                        onChange={handleChange}
                        label="Mostra codice EAN"
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Check
                        type="checkbox"
                        name="mostra_note"
                        checked={formData.mostra_note}
                        onChange={handleChange}
                        label="Mostra note prodotto"
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* TAB 4: Selezione Film (solo cataloghi a lista manuale) */}
            {!dinamico && (
            <Tab eventKey="films" title={`🎬 Film (${selectedFilms.length})`}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Film Selezionati</h5>
                    <Button variant="primary" onClick={openAddFilmsModal}>
                      <FontAwesomeIcon icon={faPlus} /> Aggiungi Film
                    </Button>
                  </div>

                  {selectedFilms.length === 0 ? (
                    <Alert variant="info">
                      Nessun film aggiunto. Clicca su "Aggiungi Film" per iniziare.
                    </Alert>
                  ) : (
                    <>
                      <Alert variant="secondary">
                        <small>💡 Trascina i film per riordinarli. L'ordine qui definito sarà mantenuto nel PDF.</small>
                      </Alert>
                      <ListGroup>
                        {selectedFilms.map((film, index) => (
                          <DraggableFilmItem
                            key={film.id}
                            film={film}
                            index={index}
                            moveFilm={moveFilm}
                            onRemove={handleRemoveFilm}
                          />
                        ))}
                      </ListGroup>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            )}
          </Tabs>

          {/* Bottoni Azione */}
          <div className="d-flex justify-content-end gap-2 mt-4">
            {dinamico && (
              <Button variant="outline-primary" onClick={apriAnteprimaPdf} disabled={generandoAnteprimaPdf || !anteprima || anteprima.totale === 0}
                title="Apre un PDF di prova con copertina, indice e le prime pagine, senza salvare">
                {generandoAnteprimaPdf ? <><Spinner animation="border" size="sm" /> Genero...</> : '👁 Anteprima PDF'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/cataloghi')}>
              Annulla
            </Button>
            <Button type="submit" variant="success" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" /> Salvataggio...
                </>
              ) : (
                isEditMode ? 'Aggiorna Catalogo' : 'Crea Catalogo'
              )}
            </Button>
          </div>
        </Form>

        {/* Modal Aggiungi Film */}
        <Modal show={showAddFilmsModal} onHide={handleCloseAddFilmsModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Aggiungi Film al Catalogo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs activeKey={searchMode} onSelect={(k) => setSearchMode(k)} className="mb-3">
              
              {/* Tab Ricerca Rapida */}
              <Tab eventKey="quick" title="🔍 Ricerca Rapida">
                <Form.Group className="mb-3">
                  <Form.Label>Cerca Film per Titolo</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      placeholder="Cerca per titolo..."
                      value={filmSearchQuery}
                      onChange={(e) => setFilmSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearchFilms()}
                    />
                    <Button onClick={handleSearchFilms} disabled={searchingFilms}>
                      {searchingFilms ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faSearch} />
                      )}
                    </Button>
                  </div>
                </Form.Group>

                {filmSearchResults.length > 0 ? (
                  <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {filmSearchResults.map(film => {
                      const isAlreadyAdded = giaNelCatalogo(film);
                      return (
                        <ListGroup.Item
                          key={film.id}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <strong>{film.titolo}</strong>
                            <br />
                            <small className="text-muted">
                              {film.anno && `${film.anno} • `}
                              {film.genere && `${film.genere} • `}
                              {film.supporto && film.supporto}
                            </small>
                          </div>
                          <Button
                            size="sm"
                            variant={isAlreadyAdded ? "secondary" : "primary"}
                            onClick={() => handleAddFilm(film)}
                            disabled={isAlreadyAdded}
                          >
                            {isAlreadyAdded ? 'Già aggiunto' : 'Aggiungi'}
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <Alert variant="info">
                    Usa la barra di ricerca per trovare film da aggiungere.
                  </Alert>
                )}
              </Tab>

              {/* Tab Filtri Avanzati */}
              <Tab eventKey="advanced" title="🎯 Filtri Avanzati">
                <Card className="mb-3">
                  <Card.Body>
                    {/* Prima riga: Titolo, Attori, Regista */}
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Titolo</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Cerca per titolo..."
                            value={advancedFilters.titolo}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, titolo: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Attori</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Nome attori..."
                            value={advancedFilters.attori}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, attori: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Regista</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Nome regista..."
                            value={advancedFilters.regista}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, regista: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {/* Seconda riga: Genere, Supporto, Condizione */}
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Genere</Form.Label>
                          <Form.Select
                            value={advancedFilters.genere}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, genere: e.target.value }))}
                            disabled={loadingDropdowns}
                          >
                            <option value="">Tutti i generi</option>
                            {generiOptions.map(genere => (
                              <option key={genere} value={genere}>{genere}</option>
                            ))}
                          </Form.Select>
                          {loadingDropdowns && <small className="text-muted">Caricamento...</small>}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Supporto</Form.Label>
                          <Form.Select
                            value={advancedFilters.supporto}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, supporto: e.target.value }))}
                            disabled={loadingDropdowns}
                          >
                            <option value="">Tutti i supporti</option>
                            {supportiOptions.map(supporto => (
                              <option key={supporto} value={supporto}>{supporto}</option>
                            ))}
                          </Form.Select>
                          {loadingDropdowns && <small className="text-muted">Caricamento...</small>}
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Condizione</Form.Label>
                          <Form.Select
                            value={advancedFilters.condizione}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, condizione: e.target.value }))}
                            disabled={loadingDropdowns}
                          >
                            <option value="">Tutte le condizioni</option>
                            {condizioniOptions.map(condizione => (
                              <option key={condizione} value={condizione}>{condizione}</option>
                            ))}
                          </Form.Select>
                          {loadingDropdowns && <small className="text-muted">Caricamento...</small>}
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    {/* Terza riga: Anno Min/Max, Fornitore, Fuori Catalogo */}
                    <Row>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>Anno Min</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="1900"
                            value={advancedFilters.anno_min}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, anno_min: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>Anno Max</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="2026"
                            value={advancedFilters.anno_max}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, anno_max: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Fornitore</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Nome fornitore..."
                            value={advancedFilters.fornitore}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, fornitore: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className="mb-3">
                          <Form.Label>Fuori Catalogo</Form.Label>
                          <Form.Select
                            value={advancedFilters.fuori_catalogo}
                            onChange={(e) => setAdvancedFilters(prev => ({ ...prev, fuori_catalogo: e.target.value }))}
                          >
                            <option value="">Tutti</option>
                            <option value="false">No</option>
                            <option value="true">Sì</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button 
                          variant="primary" 
                          onClick={handleAdvancedSearch}
                          disabled={searchingAdvanced}
                          className="mb-3 w-100"
                        >
                          {searchingAdvanced ? (
                            <>
                              <Spinner animation="border" size="sm" /> Ricerca...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} /> Applica
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {advancedSearchResults.length > 0 && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>
                        <strong>{advancedSearchResults.length}</strong> film trovati • 
                        <strong> {selectedFilmIdsForBulkAdd.length}</strong> selezionati
                      </span>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={handleToggleAllFilms}
                      >
                        {selectedFilmIdsForBulkAdd.length === advancedSearchResults.length ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
                      </Button>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <Table striped bordered hover size="sm">
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                          <tr>
                            <th style={{ width: '40px' }}>
                              <Form.Check
                                type="checkbox"
                                checked={selectedFilmIdsForBulkAdd.length === advancedSearchResults.length}
                                onChange={handleToggleAllFilms}
                              />
                            </th>
                            <th 
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('titolo')}
                            >
                              Titolo {sortBy === 'titolo' && (sortOrder === 'asc' ? '▲' : '▼')}
                            </th>
                            <th 
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('anno')}
                            >
                              Anno {sortBy === 'anno' && (sortOrder === 'asc' ? '▲' : '▼')}
                            </th>
                            <th 
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('genere')}
                            >
                              Genere {sortBy === 'genere' && (sortOrder === 'asc' ? '▲' : '▼')}
                            </th>
                            <th 
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('regista')}
                            >
                              Regista {sortBy === 'regista' && (sortOrder === 'asc' ? '▲' : '▼')}
                            </th>
                            <th 
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              onClick={() => handleSort('supporto')}
                            >
                              Supporto {sortBy === 'supporto' && (sortOrder === 'asc' ? '▲' : '▼')}
                            </th>
                            <th>Stato</th>
                          </tr>
                        </thead>
                        <tbody>
                          {advancedSearchResults.map(film => {
                            const isAlreadyAdded = giaNelCatalogo(film);
                            const isSelected = selectedFilmIdsForBulkAdd.includes(film.id);
                            
                            return (
                              <tr 
                                key={film.id}
                                style={{ 
                                  backgroundColor: isAlreadyAdded ? '#e9ecef' : (isSelected ? '#e7f3ff' : 'transparent'),
                                  cursor: 'pointer'
                                }}
                                onClick={() => !isAlreadyAdded && handleToggleFilmSelection(film.id)}
                              >
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Form.Check
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleFilmSelection(film.id)}
                                    disabled={isAlreadyAdded}
                                  />
                                </td>
                                <td>
                                  <strong>{film.titolo}</strong>
                                  {isAlreadyAdded && <Badge bg="secondary" className="ms-2">Già nel catalogo</Badge>}
                                </td>
                                <td>{film.anno || '-'}</td>
                                <td>{film.genere || '-'}</td>
                                <td>
                                  <small>{film.regista ? 
                                    (film.regista.length > 30 ? film.regista.substring(0, 30) + '...' : film.regista)
                                    : '-'
                                  }</small>
                                </td>
                                <td>
                                  <Badge 
                                    bg={
                                      film.supporto && film.supporto.includes('4K') ? 'warning' :
                                      film.supporto && film.supporto.includes('Blu') ? 'info' :
                                      film.supporto && film.supporto.includes('DVD') ? 'dark' :
                                      'secondary'
                                    }
                                  >
                                    {film.supporto || 'N/D'}
                                  </Badge>
                                </td>
                                <td>
                                  {film.fuori_catalogo ? (
                                    <Badge bg="warning">Fuori Catalogo</Badge>
                                  ) : (
                                    <Badge bg="success">Disponibile</Badge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                    <div className="d-flex justify-content-end mt-3">
                      <Button
                        variant="success"
                        onClick={handleAddSelectedFilms}
                        disabled={selectedFilmIdsForBulkAdd.length === 0}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Aggiungi {selectedFilmIdsForBulkAdd.length} Film Selezionati
                      </Button>
                    </div>
                  </>
                )}

                {advancedSearchResults.length === 0 && searchingAdvanced === false && (
                  <Alert variant="info">
                    Imposta i filtri e clicca su "Applica Filtri" per cercare film.
                  </Alert>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAddFilmsModal}>
              Chiudi
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DndProvider>
  );
}

export default CatalogoEdit;
